import { useQuery } from "@tanstack/react-query";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { Skeleton } from "@/components/ui/skeleton";
import {
  Cake,
  Heart,
  UserMinus,
  ArrowRight,
  Eye,
} from "lucide-react";
import { Link } from "wouter";
import { usePermissions } from "@/hooks/usePermissions";

interface AgingOffDependent {
  dependentId: number;
  dependentName: string;
  dateOfBirth: string;
  turnsAge: number;
  ageOutDate: string;
  daysUntilAgeOut: number;
  employeeId: number;
  employeeName: string;
  employeeEmail: string | null;
  clientId: number;
  clientName: string;
  relationship: string;
}

interface MedicareEligible {
  employeeId: number;
  employeeName: string;
  employeeEmail: string | null;
  dateOfBirth: string;
  turns65Date: string;
  daysUntil65: number;
  clientId: number;
  clientName: string;
}

interface AgeTrackingResponse {
  agingOffDependents: AgingOffDependent[];
  medicareEligible: MedicareEligible[];
  totalAgingOff: number;
  totalMedicare: number;
}

function getUrgencyBadge(daysUntil: number, type: "aging-off" | "medicare") {
  if (daysUntil <= 7) {
    return (
      <Badge variant="destructive" className="text-xs">
        {daysUntil === 0 ? "Today" : `${daysUntil}d left`}
      </Badge>
    );
  }
  if (daysUntil <= 30) {
    return (
      <Badge variant="destructive" className="bg-orange-100 text-orange-800 hover:bg-orange-100 text-xs">
        {daysUntil}d left
      </Badge>
    );
  }
  return (
    <Badge variant="outline" className="text-xs">
      {daysUntil}d
    </Badge>
  );
}

function formatDate(dateStr: string): string {
  return new Date(dateStr).toLocaleDateString("en-US", {
    month: "short",
    day: "numeric",
    year: "numeric",
  });
}

export function UpcomingAgeEvents() {
  const { can, isLoading: permissionsLoading } = usePermissions();
  const hasPermission = can("clients:view");

  const { data, isLoading } = useQuery<AgeTrackingResponse>({
    queryKey: ["/api/agency/age-tracking", { days: 60 }],
    queryFn: async () => {
      const response = await fetch("/api/agency/age-tracking?days=60");
      if (!response.ok) throw new Error("Failed to fetch");
      return response.json();
    },
    enabled: hasPermission,
  });

  // Don't render if user doesn't have permission
  if (!permissionsLoading && !hasPermission) {
    return null;
  }

  if (isLoading) {
    return (
      <Card>
        <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
          <CardTitle className="text-sm font-medium">
            Upcoming Age Events
          </CardTitle>
          <Cake className="h-4 w-4 text-muted-foreground" />
        </CardHeader>
        <CardContent>
          <div className="space-y-4">
            {[...Array(3)].map((_, i) => (
              <div key={i} className="flex items-start gap-3">
                <Skeleton className="h-8 w-8 rounded-full" />
                <div className="flex-1 space-y-2">
                  <Skeleton className="h-4 w-3/4" />
                  <Skeleton className="h-3 w-1/2" />
                </div>
              </div>
            ))}
          </div>
        </CardContent>
      </Card>
    );
  }

  const agingOff = data?.agingOffDependents || [];
  const medicare = data?.medicareEligible || [];
  const hasEvents = agingOff.length > 0 || medicare.length > 0;

  // Combine and sort all events by days until
  const allEvents = [
    ...agingOff.map((d) => ({
      type: "aging-off" as const,
      id: `dep-${d.dependentId}`,
      name: d.dependentName,
      parentName: d.employeeName,
      date: d.ageOutDate,
      daysUntil: d.daysUntilAgeOut,
      clientId: d.clientId,
      clientName: d.clientName,
      employeeId: d.employeeId,
      relationship: d.relationship,
    })),
    ...medicare.map((e) => ({
      type: "medicare" as const,
      id: `emp-${e.employeeId}`,
      name: e.employeeName,
      parentName: null,
      date: e.turns65Date,
      daysUntil: e.daysUntil65,
      clientId: e.clientId,
      clientName: e.clientName,
      employeeId: e.employeeId,
      relationship: null,
    })),
  ].sort((a, b) => a.daysUntil - b.daysUntil);

  // Limit to 5 items for widget
  const displayEvents = allEvents.slice(0, 5);

  // Count urgent events (within 30 days)
  const urgentCount = allEvents.filter((e) => e.daysUntil <= 30).length;

  return (
    <Card className={urgentCount > 0 ? "border-orange-200" : ""}>
      <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
        <div className="flex items-center gap-2">
          <CardTitle className="text-sm font-medium">
            Upcoming Age Events
          </CardTitle>
          {urgentCount > 0 && (
            <Badge variant="destructive" className="text-xs">
              {urgentCount} urgent
            </Badge>
          )}
        </div>
        <Cake className="h-4 w-4 text-muted-foreground" />
      </CardHeader>
      <CardContent>
        {!hasEvents ? (
          <div className="text-center py-6">
            <Cake className="h-8 w-8 text-gray-400 mx-auto mb-2" />
            <p className="text-sm text-gray-500">No upcoming age events</p>
            <p className="text-xs text-gray-400 mt-1">
              Age 26 and Medicare events will appear here
            </p>
          </div>
        ) : (
          <div className="space-y-3">
            {displayEvents.map((event) => (
              <div
                key={event.id}
                className="flex items-center justify-between p-3 border rounded-lg hover:bg-gray-50 dark:hover:bg-gray-800 transition-colors"
              >
                <div className="flex items-center gap-3 flex-1 min-w-0">
                  <div className="mt-0.5">
                    {event.type === "aging-off" ? (
                      <UserMinus className="h-4 w-4 text-orange-500" />
                    ) : (
                      <Heart className="h-4 w-4 text-blue-500" />
                    )}
                  </div>
                  <div className="flex-1 min-w-0">
                    <div className="flex items-center space-x-2">
                      <p className="text-sm font-medium text-gray-900 dark:text-gray-100 truncate">
                        {event.name}
                      </p>
                      <Badge
                        className={`text-xs ${
                          event.type === "aging-off"
                            ? "bg-orange-100 text-orange-800 dark:bg-orange-900 dark:text-orange-300"
                            : "bg-blue-100 text-blue-800 dark:bg-blue-900 dark:text-blue-300"
                        }`}
                      >
                        {event.type === "aging-off" ? "Turns 26" : "Medicare"}
                      </Badge>
                      {getUrgencyBadge(event.daysUntil, event.type)}
                    </div>
                    <div className="flex items-center text-xs text-gray-500 mt-1 space-x-4">
                      <span>{event.clientName}</span>
                      <span>{formatDate(event.date)}</span>
                      {event.parentName && (
                        <span>({event.relationship} of {event.parentName})</span>
                      )}
                    </div>
                  </div>
                </div>
                <Link
                  href={`/clients/${event.clientId}/employees/${event.employeeId}`}
                >
                  <Button variant="ghost" size="sm">
                    <Eye className="h-4 w-4" />
                  </Button>
                </Link>
              </div>
            ))}
          </div>
        )}

        {hasEvents && allEvents.length > 5 && (
          <div className="mt-4 pt-3 border-t">
            <Link href="/age-events">
              <Button variant="ghost" size="sm" className="w-full">
                View All Events ({allEvents.length})
                <ArrowRight className="h-4 w-4 ml-2" />
              </Button>
            </Link>
          </div>
        )}
      </CardContent>
    </Card>
  );
}
